import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, MapPin } from 'lucide-react';
import api, { LOCATIONS, trackEvent } from '@/lib/api';
import { motion } from 'framer-motion';

const TYPE_LABELS = { new_build: 'New Build', renovation: 'Renovation', commercial: 'Commercial' };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    trackEvent('page_view', { page: 'projects' });
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let result = projects;
    if (typeFilter !== 'all') result = result.filter(p => p.project_type === typeFilter);
    if (locationFilter !== 'all') result = result.filter(p => p.location === locationFilter);
    setFiltered(result);
  }, [projects, typeFilter, locationFilter]);

  const locationLabel = (loc) => LOCATIONS.find(l => l.value === loc)?.label || loc?.replace('_', ' ');

  if (selectedProject) {
    return (
      <div className="min-h-screen bg-brand-light pt-20" data-testid="project-detail">
        <div className="section-dark py-12 px-6">
          <div className="max-w-5xl mx-auto">
            <button onClick={() => setSelectedProject(null)} className="text-white/50 hover:text-white text-sm font-body mb-4 inline-block" data-testid="project-back-btn">
              &larr; Back to Projects
            </button>
            <p className="text-brand-accent font-heading text-xs font-bold uppercase tracking-[0.2em] mb-2">{locationLabel(selectedProject.location)}</p>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tighter font-medium text-white mb-2">{selectedProject.title}</h1>
            <div className="flex items-center gap-3 mt-3">
              <Badge variant="outline" className="text-white/60 border-white/20 text-xs uppercase">{TYPE_LABELS[selectedProject.project_type] || selectedProject.project_type}</Badge>
              {selectedProject.style && <Badge variant="outline" className="text-white/60 border-white/20 text-xs">{selectedProject.style}</Badge>}
              <Badge variant="outline" className="text-brand-accent/80 border-brand-accent/30 text-xs">{selectedProject.budget_range}</Badge>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-12">
          {/* Image Gallery */}
          {selectedProject.images?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10" data-testid="project-gallery">
              {selectedProject.images.map((img, i) => (
                <div key={i} className={`overflow-hidden rounded-sm ${i === 0 && selectedProject.images.length > 1 ? 'md:col-span-2' : ''}`}>
                  <img src={img} alt={`${selectedProject.title} ${i + 1}`} className="w-full h-auto object-cover" style={{ maxHeight: i === 0 ? '500px' : '350px' }} />
                </div>
              ))}
            </div>
          )}

          {/* Subtitle — short caption used directly under the main image */}
          {(selectedProject.subtitle || selectedProject.description) && (
            <div className="max-w-3xl" data-testid="project-subtitle">
              <p className="font-body text-brand-dark/70 text-base md:text-lg leading-relaxed">
                {selectedProject.subtitle || selectedProject.description}
              </p>
            </div>
          )}

          {/* Long description — full project narrative, paragraph-spaced, readable column */}
          {selectedProject.description_long && (
            <div
              className="mt-12 max-w-3xl font-body text-brand-dark/70 text-base leading-relaxed space-y-5"
              data-testid="project-description-long"
            >
              {selectedProject.description_long.split(/\n{2,}/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 p-8 bg-brand-dark rounded-sm text-center">
            <p className="text-brand-accent/60 font-heading italic text-sm mb-3">We create space — by turning ideas into clarity.</p>
            <h3 className="font-heading text-lg font-medium text-white mb-4">Inspired by this project?</h3>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/feasibility" data-testid="project-cta-feasibility">
                <Button className="bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm font-body text-xs font-semibold uppercase tracking-wider">
                  Check Your Feasibility <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
              <Link to="/architect" data-testid="project-cta-architect">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm font-body text-xs font-semibold uppercase tracking-wider">
                  Start Guided Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-light pt-20" data-testid="projects-page">
      {/* Header */}
      <div className="section-dark py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-brand-accent font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Selected Work</p>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tighter font-medium text-white mb-4">Our Projects</h1>
          <p className="text-white/50 font-body text-base max-w-2xl">
            Each project begins with understanding — the site, the people, the purpose. These are spaces we've created.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-10" data-testid="project-filters">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="filter-type" className="w-48 rounded-sm h-10 bg-white">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="new_build">New Build</SelectItem>
              <SelectItem value="renovation">Renovation</SelectItem>
            </SelectContent>
          </Select>
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger data-testid="filter-location" className="w-48 rounded-sm h-10 bg-white">
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-brand-dark/40 text-sm font-body self-center">{filtered.length} project{filtered.length !== 1 ? 's' : ''}</p>
        </div>

        {/* Project Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-brand-dark/40 font-body">No projects match the current filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((project, i) => (
              <motion.div
                key={project.project_id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                onClick={() => setSelectedProject(project)}
                className="group cursor-pointer project-card rounded-sm overflow-hidden bg-white border border-black/5"
                data-testid={`project-card-${i}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={project.images?.[0] || '/projects/house-bosman.jpg'}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {project.featured && (
                    <span className="absolute top-3 right-3 bg-brand-accent text-brand-dark text-xs font-body font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm">
                      Featured
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-brand-dark/40 text-xs font-body mb-2">
                    <MapPin className="w-3 h-3" />
                    <span>{locationLabel(project.location)}</span>
                    <span className="text-brand-dark/20">|</span>
                    <span className="capitalize">{TYPE_LABELS[project.project_type] || project.project_type}</span>
                  </div>
                  <h3 className="font-heading text-lg font-medium text-brand-dark group-hover:text-brand-accent-dark transition-colors">{project.title}</h3>
                  <p className="text-brand-dark/50 text-sm font-body mt-1.5 line-clamp-2">{project.subtitle || project.description}</p>
                  <div className="flex items-center justify-between mt-4">
                    {project.style && <Badge variant="outline" className="text-xs text-brand-dark/40 border-black/10">{project.style}</Badge>}
                    <span className="text-xs font-body text-brand-accent-dark font-medium">{project.budget_range}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
